import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RefreshCw, LogIn, Power, Shield, Clock, Car, Mail, Eye, EyeOff, Save, Loader2, Pencil, ChevronDown, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VinSolutionsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [dealerships, setDealerships] = useState([]);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [selectedDealership, setSelectedDealership] = useState('');
  const [dlrCreds, setDlrCreds] = useState({});
  const [editingDlrCreds, setEditingDlrCreds] = useState({});
  const [dlrCredForms, setDlrCredForms] = useState({});
  const [savingDlrCreds, setSavingDlrCreds] = useState({});
  const [showDlrPassword, setShowDlrPassword] = useState({});
  const [expandedDlrs, setExpandedDlrs] = useState({});
  const [manual2fa, setManual2fa] = useState('');
  const [submitting2fa, setSubmitting2fa] = useState(false);

  const handleSubmit2fa = async () => {
    if (!selectedDealership) { toast.error('Select a dealership first'); return; }
    if (manual2fa.trim().length !== 6) { toast.error('Code must be 6 digits'); return; }
    setSubmitting2fa(true);
    try {
      await axios.post(`${API}/api/vinsolutions/manual-2fa`, { dealership_id: selectedDealership, code: manual2fa.trim() }, { headers });
      const dlr = dealerships.find(d => d.dealership_id === selectedDealership);
      toast.success(`2FA code saved for ${dlr?.name || selectedDealership} — will be used on next login attempt`);
      setManual2fa('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save 2FA code');
    } finally {
      setSubmitting2fa(false);
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const [dlrRes, gmailRes] = await Promise.all([
        axios.get(`${API}/api/dealerships`, { headers }),
        axios.get(`${API}/api/vinsolutions/test-2fa-gmail`, { headers }).catch(() => ({ data: null })),
      ]);
      setGmailStatus(gmailRes.data);
      const dlrs = Array.isArray(dlrRes.data) ? dlrRes.data : dlrRes.data.dealerships || [];
      const vsDlrs = dlrs.filter(d => d.crm === 'vinsolutions');
      setDealerships(vsDlrs);
      const activeDlrId = selectedDealership || (vsDlrs.length > 0 ? vsDlrs[0].dealership_id : '');
      if (!selectedDealership && activeDlrId) {
        setSelectedDealership(activeDlrId);
      }
      // Fetch status, gmail, and all credentials in parallel
      const promises = [];
      if (activeDlrId) {
        promises.push(axios.get(`${API}/api/vinsolutions/status?dealership_id=${activeDlrId}`, { headers }).then(r => setStatus(r.data)));
        promises.push(axios.get(`${API}/api/vinsolutions/test-2fa-gmail?dealership_id=${activeDlrId}`, { headers }).then(r => setGmailStatus(r.data)).catch(() => {}));
      } else {
        promises.push(axios.get(`${API}/api/vinsolutions/status`, { headers }).then(r => setStatus(r.data)));
      }
      for (const dlr of vsDlrs) {
        promises.push(
          axios.get(`${API}/api/vinsolutions/credentials/${dlr.dealership_id}`, { headers })
            .then(credRes => setDlrCreds(prev => ({ ...prev, [dlr.dealership_id]: credRes.data })))
            .catch(() => {})
        );
      }
      await Promise.all(promises);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, selectedDealership]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Refresh status when dealership selection changes
  useEffect(() => {
    if (selectedDealership && !loading) {
      axios.get(`${API}/api/vinsolutions/status?dealership_id=${selectedDealership}`, { headers })
        .then(res => setStatus(res.data))
        .catch(() => {});
      axios.get(`${API}/api/vinsolutions/test-2fa-gmail?dealership_id=${selectedDealership}`, { headers })
        .then(res => setGmailStatus(res.data))
        .catch(() => setGmailStatus(null));
    }
  }, [selectedDealership]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_2fa_connected') === 'true') {
      toast.success('Gmail 2FA account connected successfully');
      window.history.replaceState({}, '', window.location.pathname);
      fetchStatus();
    }
  }, []);

  const handleForceLogin = async () => {
    if (!selectedDealership) {
      toast.error('Select a dealership first');
      return;
    }
    setActionLoading('login');
    try {
      // If manual 2FA code is provided, submit it first
      if (manual2fa.trim().length === 6) {
        await axios.post(`${API}/api/vinsolutions/manual-2fa`, { dealership_id: selectedDealership, code: manual2fa.trim() }, { headers });
      }
      const res = await axios.post(`${API}/api/vinsolutions/force-login`, { dealership_id: selectedDealership }, { headers });
      if (res.data.success) {
        const dlr = dealerships.find(d => d.dealership_id === selectedDealership);
        toast.success(res.data.message || `VinSolutions login initiated${dlr ? ` for ${dlr.name}` : ''} — check status in 30-60 seconds`);
        setManual2fa('');
      } else {
        toast.error('Login failed — check credentials or 2FA');
      }
      setTimeout(() => fetchStatus(), 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleResetCooldown = async () => {
    setActionLoading('cooldown');
    try {
      await axios.post(`${API}/api/vinsolutions/reset-cooldown`, {}, { headers });
      toast.success('Cooldown reset');
      await fetchStatus();
    } catch (e) {
      toast.error('Failed to reset cooldown');
    } finally {
      setActionLoading('');
    }
  };

  const handleToggleAutoSend = async () => {
    setActionLoading('autosend');
    try {
      const newVal = !status?.auto_send_enabled;
      const payload = { enabled: newVal };
      if (selectedDealership) payload.dealership_id = selectedDealership;
      await axios.post(`${API}/api/vinsolutions/toggle-auto-send`, payload, { headers });
      const dlr = dealerships.find(d => d.dealership_id === selectedDealership);
      toast.success(`Auto-send ${newVal ? 'enabled' : 'disabled'}${dlr ? ` for ${dlr.name}` : ''}`);
      await fetchStatus();
    } catch (e) {
      toast.error('Failed to toggle auto-send');
    } finally {
      setActionLoading('');
    }
  };

  const handleReauthGmail = async () => {
    setActionLoading('gmail');
    try {
      const dlrParam = selectedDealership ? `&dealership_id=${selectedDealership}` : '';
      const res = await axios.get(`${API}/api/oauth/vinsolutions-2fa/connect?frontend_origin=${encodeURIComponent(window.location.origin)}${dlrParam}`, { headers });
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start Gmail authentication');
      setActionLoading('');
    }
  };

  const startEditDlrCreds = (dlrId) => {
    const cred = dlrCreds[dlrId] || {};
    setDlrCredForms(prev => ({ ...prev, [dlrId]: { username: cred.username || '', password: '' } }));
    setEditingDlrCreds(prev => ({ ...prev, [dlrId]: true }));
    setShowDlrPassword(prev => ({ ...prev, [dlrId]: false }));
  };

  const saveDlrCreds = async (dlrId) => {
    setSavingDlrCreds(prev => ({ ...prev, [dlrId]: true }));
    try {
      const form = dlrCredForms[dlrId] || {};
      const existing = dlrCreds[dlrId] || {};
      const payload = {};
      if (form.username !== existing.username) payload.username = form.username;
      if (form.password) payload.password = form.password;
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setEditingDlrCreds(prev => ({ ...prev, [dlrId]: false }));
        setSavingDlrCreds(prev => ({ ...prev, [dlrId]: false }));
        return;
      }
      await axios.post(`${API}/api/vinsolutions/credentials/${dlrId}`, payload, { headers });
      toast.success('Credentials updated');
      setEditingDlrCreds(prev => ({ ...prev, [dlrId]: false }));
      // Refresh creds
      const credRes = await axios.get(`${API}/api/vinsolutions/credentials/${dlrId}`, { headers });
      setDlrCreds(prev => ({ ...prev, [dlrId]: credRes.data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update credentials');
    }
    setSavingDlrCreds(prev => ({ ...prev, [dlrId]: false }));
  };

  if (loading) {
    return (
      <div className="p-8 text-slate-400" data-testid="vinsolutions-page">
        <h1 className="text-3xl font-black tracking-tight text-white mb-2">VinSolutions</h1>
        <p>Loading...</p>
      </div>
    );
  }

  const cooldownMins = status?.login_cooldown_remaining_seconds
    ? Math.ceil(status.login_cooldown_remaining_seconds / 60)
    : 0;

  const selectedDlr = dealerships.find(d => d.dealership_id === selectedDealership);
  const selectedCreds = dlrCreds[selectedDealership] || {};

  return (
    <div className="p-8" data-testid="vinsolutions-page">
      <h1 className="text-3xl font-black tracking-tight text-white mb-1">VinSolutions</h1>
      <p className="text-slate-500 text-sm mb-8">CRM browser automation status and controls</p>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Session Status */}
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-5">
          <div className="flex items-center gap-3 mb-4">
            <Power className="w-5 h-5 text-slate-400" />
            <h3 className="text-sm font-bold tracking-wider uppercase text-slate-400">Session</h3>
          </div>
          {selectedDlr && (
            <p className="text-[10px] text-slate-600 mb-3 truncate">{selectedDlr.name}</p>
          )}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Browser</span>
              <Badge className={status?.browser_running ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                {status?.browser_running ? 'Running' : 'Stopped'}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Logged In</span>
              <Badge className={status?.logged_in ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                {status?.logged_in ? 'Connected' : 'Disconnected'}
              </Badge>
            </div>
            {cooldownMins > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Cooldown</span>
                <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                  {cooldownMins} min remaining
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Credentials - reflects selected dealership */}
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-5">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-slate-400" />
            <h3 className="text-sm font-bold tracking-wider uppercase text-slate-400">Credentials</h3>
          </div>
          {selectedDlr && (
            <p className="text-[10px] text-slate-600 mb-3 truncate">{selectedDlr.name}</p>
          )}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Username</span>
              <Badge className={selectedCreds.username ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                {selectedCreds.username ? 'Configured' : 'Missing'}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Password</span>
              <Badge className={selectedCreds.password_set ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                {selectedCreds.password_set ? 'Configured' : 'Missing'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-5">
          <div className="flex items-center gap-3 mb-4">
            <Car className="w-5 h-5 text-slate-400" />
            <h3 className="text-sm font-bold tracking-wider uppercase text-slate-400">Auto-Send</h3>
          </div>
          {selectedDlr && (
            <p className="text-[10px] text-slate-600 mb-3 truncate">{selectedDlr.name}</p>
          )}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Auto-send pending leads</span>
              <Switch
                checked={status?.auto_send_enabled || false}
                onCheckedChange={handleToggleAutoSend}
                disabled={actionLoading === 'autosend'}
                data-testid="vs-auto-send-toggle"
              />
            </div>
            <p className="text-xs text-slate-600">
              When enabled, the system automatically sends pending CRM leads via the browser session.
            </p>
          </div>
        </div>

        {/* Gmail 2FA Connection */}
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-5">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-5 h-5 text-slate-400" />
            <h3 className="text-sm font-bold tracking-wider uppercase text-slate-400">Gmail (2FA)</h3>
          </div>
          {selectedDlr && (
            <p className="text-[10px] text-slate-600 mb-3 truncate">{selectedDlr.name}</p>
          )}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Connection</span>
              <Badge className={gmailStatus?.success ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                {gmailStatus?.success ? 'Connected' : 'Failed'}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Account</span>
              <span className="text-xs text-slate-500 truncate max-w-[140px]">{gmailStatus?.gmail_email || 'N/A'}</span>
            </div>
            {gmailStatus?.success && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Inbox Access</span>
                <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                  {gmailStatus.messages_accessible} messages
                </Badge>
              </div>
            )}
            {gmailStatus && !gmailStatus.success && (
              <p className="text-xs text-red-400 mt-1">{gmailStatus.error?.slice(0, 80)}</p>
            )}
            <Button
              onClick={handleReauthGmail}
              disabled={actionLoading === 'gmail'}
              size="sm"
              className={gmailStatus?.success
                ? "w-full mt-2 border-[#333] text-slate-400 hover:bg-[#1a1a1a] bg-transparent border"
                : "w-full mt-2 bg-red-600 hover:bg-red-700 text-white"
              }
              data-testid="vs-reauth-gmail-btn"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              {gmailStatus?.success ? 'Re-authenticate' : 'Connect Gmail'}
            </Button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-5 mb-8">
        <h3 className="text-sm font-bold tracking-wider uppercase text-slate-400 mb-4">Actions</h3>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={selectedDealership} onValueChange={setSelectedDealership}>
            <SelectTrigger className="w-[260px] bg-[#111] border-[#333] h-10 text-sm" data-testid="vs-dealership-select">
              <SelectValue placeholder="Select dealership" />
            </SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626]">
              {dealerships.map(dlr => (
                <SelectItem key={dlr.dealership_id} value={dlr.dealership_id} className="text-white hover:bg-[#262626]">
                  {dlr.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            onClick={handleForceLogin}
            disabled={actionLoading === 'login' || !selectedDealership}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="vs-force-login-btn"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {actionLoading === 'login' ? 'Logging in...' : 'Login to VinSolutions'}
          </Button>

          {cooldownMins > 0 && (
            <Button
              onClick={handleResetCooldown}
              disabled={actionLoading === 'cooldown'}
              variant="outline"
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              data-testid="vs-reset-cooldown-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              {actionLoading === 'cooldown' ? 'Resetting...' : 'Reset Cooldown'}
            </Button>
          )}

          <Button
            onClick={fetchStatus}
            variant="outline"
            className="border-[#333] text-slate-400 hover:bg-[#1a1a1a]"
            data-testid="vs-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        </div>

        {/* Manual 2FA Code Input */}
        <div className="mt-4 pt-4 border-t border-[#262626]">
          <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Manual 2FA Code</Label>
          <div className="flex items-center gap-3">
            <Input
              type="text"
              maxLength={6}
              placeholder="Enter 6-digit code"
              value={manual2fa}
              onChange={e => setManual2fa(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-[180px] bg-[#111] border-[#333] text-white text-center tracking-widest text-lg font-mono"
              data-testid="vs-manual-2fa-input"
            />
            <Button
              onClick={handleSubmit2fa}
              disabled={submitting2fa || manual2fa.length !== 6 || !selectedDealership}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="vs-submit-2fa-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              {submitting2fa ? 'Saving...' : 'Submit Code'}
            </Button>
            <span className="text-xs text-slate-600">Enter the code then click Login, or submit and wait for auto-login</span>
          </div>
        </div>
      </div>

      {/* Dealerships Using VinSolutions — with per-dealership credentials */}
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-5">
        <h3 className="text-sm font-bold tracking-wider uppercase text-slate-400 mb-4">
          Dealerships Using VinSolutions
        </h3>
        {dealerships.length === 0 ? (
          <p className="text-slate-600 text-sm">No dealerships configured with VinSolutions CRM.</p>
        ) : (
          <div className="space-y-3">
            {dealerships.map(dlr => {
              const dlrId = dlr.dealership_id;
              const isExpanded = expandedDlrs[dlrId];
              const cred = dlrCreds[dlrId] || {};
              const isEditing = editingDlrCreds[dlrId];
              const form = dlrCredForms[dlrId] || { username: '', password: '' };
              const isSaving = savingDlrCreds[dlrId];
              const showPwd = showDlrPassword[dlrId];

              return (
                <div key={dlrId} className="bg-[#111] rounded-lg border border-[#1a1a1a] overflow-hidden" data-testid={`vs-dlr-${dlrId}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedDlrs(prev => ({ ...prev, [dlrId]: !prev[dlrId] }))}
                    className="w-full flex items-center justify-between p-3 hover:bg-[#161616] transition-colors"
                    data-testid={`vs-dlr-toggle-${dlrId}`}
                  >
                    <div className="text-left">
                      <p className="text-white font-medium text-sm">{dlr.name}</p>
                      <p className="text-slate-500 text-xs mt-0.5">
                        {(dlr.email_aliases || []).length} alias{(dlr.email_aliases || []).length !== 1 ? 'es' : ''}
                        {cred.username ? ` · ${cred.username}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={cred.password_set ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                        {cred.password_set ? 'Credentials Set' : 'No Credentials'}
                      </Badge>
                      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-[#1a1a1a] p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Login Credentials</h4>
                        {!isEditing && (
                          <Button
                            onClick={() => startEditDlrCreds(dlrId)}
                            variant="outline"
                            size="sm"
                            className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 hover:text-white text-[10px] font-bold tracking-widest uppercase h-7"
                            data-testid={`vs-edit-creds-${dlrId}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        )}
                      </div>

                      {isEditing ? (
                        <div className="space-y-3">
                          <div>
                            <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Username</Label>
                            <Input
                              value={form.username}
                              onChange={e => setDlrCredForms(prev => ({ ...prev, [dlrId]: { ...form, username: e.target.value } }))}
                              placeholder="VinSolutions username"
                              className="mt-1 bg-[#0A0A0A] border-[#333] h-9 text-sm text-white font-mono placeholder:text-slate-600"
                              data-testid={`vs-username-${dlrId}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Password</Label>
                            <div className="relative mt-1">
                              <Input
                                type={showPwd ? 'text' : 'password'}
                                value={form.password}
                                onChange={e => setDlrCredForms(prev => ({ ...prev, [dlrId]: { ...form, password: e.target.value } }))}
                                placeholder="Enter new password (leave blank to keep current)"
                                className="bg-[#0A0A0A] border-[#333] h-9 text-sm text-white font-mono pr-10 placeholder:text-slate-600"
                                data-testid={`vs-password-${dlrId}`}
                              />
                              <button
                                type="button"
                                onClick={() => setShowDlrPassword(prev => ({ ...prev, [dlrId]: !prev[dlrId] }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                              >
                                {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <Button
                              onClick={() => saveDlrCreds(dlrId)}
                              disabled={isSaving}
                              className="bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] tracking-widest uppercase h-8 px-4"
                              data-testid={`vs-save-creds-${dlrId}`}
                            >
                              {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> Save</>}
                            </Button>
                            <Button
                              onClick={() => setEditingDlrCreds(prev => ({ ...prev, [dlrId]: false }))}
                              variant="outline"
                              className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 hover:text-white font-bold text-[10px] tracking-widest uppercase h-8"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center p-2.5 bg-[#0A0A0A] rounded border border-[#1a1a1a]">
                            <span className="text-xs text-slate-400">Username</span>
                            <span className="text-xs text-white font-mono">{cred.username || '—'}</span>
                          </div>
                          <div className="flex justify-between items-center p-2.5 bg-[#0A0A0A] rounded border border-[#1a1a1a]">
                            <span className="text-xs text-slate-400">Password</span>
                            <span className="text-xs text-white font-mono">{cred.password_masked || '—'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
